import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Pie, Doughnut, Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
} from 'chart.js';
import { Link } from 'react-router-dom';
import './crm.css';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
);

const NewPage = () => {
  const [employeeData, setEmployeeData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
      },
    ],
  });

  const [hrPieData, setHrPieData] = useState({
    labels: ['Leaves', 'Termination Required', 'Productivity'],
    datasets: [
      {
        data: [0, 0, 0],
        backgroundColor: ['#D0A6C4', '#A2D2FF', '#FAD02E'],
      },
    ],
  });

  const [projectData, setProjectData] = useState({
    labels: ['Active', 'Pending', 'Completed'],
    datasets: [
      {
        data: [50, 30, 20],
        backgroundColor: ['#D0A6C4', '#A2D2FF', '#FAD02E'],
      },
    ],
  });

  const [salesData, setSalesData] = useState({
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    datasets: [
      {
        label: 'Sales',
        data: [120, 150, 180, 200], // Mock sales data
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.2)',
        fill: true,
      },
    ],
  });

  const [financialData, setFinancialData] = useState({
    labels: ['January', 'February', 'March', 'April'],
    datasets: [
      {
        label: 'Revenue',
        data: [5000, 7000, 9000, 11000], // Mock financial data
        backgroundColor: '#FF9800',
      },
      {
        label: 'Expenses',
        data: [6000, 4000, 5000, 6000], // Mock expenses data
        backgroundColor: '#FF6347',
      },
    ],
  });

  const [supportData, setSupportData] = useState({
    labels: ['Open', 'Closed', 'Pending'],
    datasets: [
      {
        data: [10, 15, 5], // Mock support data
        backgroundColor: ['#4CAF50', '#FF6347', '#FF9800'],
      },
    ],
  });

  const fetchEmployeeProductivityData = async () => {
    try {
      const response = await axios.get('https://cleezoclass.com:8443/api/employee-productivity-managers');
      const data = response.data.data;

      if (Array.isArray(data)) {
        const labels = data.map((item) => item.project_name);
        const totalProductivityData = data.map((item) => parseFloat(item.total_productivity));

        // Dynamically set colors based on productivity change
        const colors = totalProductivityData.map((prod) => {
          if (prod >= 75) return '#4CAF50'; // Green for high productivity
          if (prod >= 50) return '#FF9800'; // Orange for medium productivity
          return '#FF6347'; // Red for low productivity
        });

        setEmployeeData({
          labels: labels,
          datasets: [
            {
              data: totalProductivityData,
              backgroundColor: colors,
            },
          ],
        });
      }
    } catch (error) {
      console.error('Error fetching employee productivity data:', error);
    }
  };

  const fetchHRData = async () => {
    try {
      const leaveResponse = await axios.get('https://cleezoclass.com:8443/api/total-leaves');
      const terminationResponse = await axios.get('https://cleezoclass.com:8443/api/termination-required');
      const productivityResponse = await axios.get('https://cleezoclass.com:8443/api/total-productivity');
    
      const { approved, pending, rejected } = leaveResponse.data.data;
      const terminationRequired = parseInt(terminationResponse.data.data.terminationRequired, 10) || 0;
      const totalProductivity = Number(productivityResponse.data.data.totalProductivity);
    
      const totalLeaves = Number(approved) + Number(pending) + Number(rejected);
  
      // Update HR pie chart data
      setHrPieData({
        labels: ['Leaves', 'Termination Required', 'Productivity'],
        datasets: [
          {
            data: [totalLeaves, terminationRequired, totalProductivity],
            backgroundColor: [
              totalLeaves > 50 ? '#FF6347' : '#4CAF50', // Red if leaves are high, green otherwise
              terminationRequired > 10 ? '#FF6347' : '#FF9800', // Red for high termination, orange otherwise
              totalProductivity > 70 ? '#4CAF50' : '#FF9800', // Green for high productivity, orange otherwise
            ],
          },
        ],
      });
    } catch (error) {
      console.error('Error fetching HR data:', error);
    }
  };

  const fetchProjectData = async () => {
    try {
      const response = await axios.get('https://cleezoclass.com:8443/api/project-status');
      const data = response.data.data;

      // Assuming the API returns an object with project status as percentages (Active, Pending, Completed)
      const { active, pending, completed } = data;

      // Dynamically set colors based on project status
      const projectColors = [
        pending > 50 ? '#FF6347' : '#FF9800',  // Red for Pending > 50%, Orange otherwise
        active > 50 ? '#FF9800' : '#FF6347',   // Orange for Active > 50%, Red otherwise
        completed > 50 ? '#4CAF50' : '#D0A6C4', // Green for Completed > 50%, Light color otherwise
      ];

      setProjectData({
        labels: ['Active', 'Pending', 'Completed'],
        datasets: [
          {
            data: [active, pending, completed],
            backgroundColor: projectColors,
          },
        ],
      });
    } catch (error) {
      console.error('Error fetching project data:', error);
    }
  };
  
  useEffect(() => {
    fetchEmployeeProductivityData();
    fetchHRData();
    fetchProjectData();
  }, []);

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.label || '';
            let value = context.raw || 0;
            return `${label}: ${value}`;
          },
        },
      },
    },
    animation: {
      animateRotate: true,
      animateScale: true,
    },
  };

  const doughnutOptions = {
    ...pieOptions,
    cutoutPercentage: 80,
  };

  return (
    <div className="crm-dashboard-container">
      {/* Header Section */}
      <div className="crm-dashboard-header">
        <h1> Cleezo class CRM</h1>
      </div>

      {/* First Row: Employee, Project, and HR/Admin Management Sections */}
      <div className="crm-dashboard-row">
        {/* Employee Management Section */}
        <div className="crm-dashboard-section">
          <h2>Employee Management</h2>
          <div className="crm-chart-container crm-chart-container-display">
            <Pie data={employeeData} options={pieOptions} width={253} height={253} />
          </div>
          <div className="crm-link-container">
            {/* here also iam adding s letter to file name at last */}
            <Link to="/Leaveportal" className="crm-link">Leave Portal</Link>
            <Link to="/Taskupdate" className="crm-link">Team Task Update</Link>
            
            <Link to="/Employeeproductivity" className="crm-link">Employee Productivity</Link>
          </div>
        </div>

        {/* Project and Task Management Section */}
        <div className="crm-dashboard-section">
          <h2>Project & Task Management</h2>
          <div className="crm-chart-container crm-chart-container-display">
            <Doughnut data={projectData} options={doughnutOptions} width={253} height={253} />
          </div>
          <div className="crm-link-container">
            <Link to="/Projectoverview" className="crm-link">Project Overview</Link>
            <Link to="/Taskassignment" className="crm-link">Task Assignment</Link>
          </div>
        </div>

        {/* HR and Admin Management Section */}
        <div className="crm-dashboard-section">
          <h2>HR & Admin Management</h2>
          <div className="crm-chart-container crm-chart-container-display">
            <Pie data={hrPieData} options={pieOptions} width={253} height={253} />
          </div>
          <div className="crm-link-container">
            {/* here iam changing the file names (at last iam adding s letter) */}
            <Link to="/LeaveManagement" className="crm-link">Leave Management</Link>
            <Link to="/Policies-s" className="crm-link">Policies</Link>
            {/* for policies also add extra s alphabet */}
            <Link to="/Announcements_s" className="crm-link">Announcements</Link>
            {/* for announcements adding extra s */}
            <Link to="/Employeeprofile" className="crm-link">Employee Profile</Link>
          </div>
        </div>
      </div>

      {/* Second Row: Marketing, Financial, and Support Sections */}
      <div className="crm-dashboard-row">
        {/* Marketing Section */}
        <div className="crm-dashboard-section">
          <h2>Marketing</h2>
          <div className="crm-chart-container crm-chart-container-display">
            <Line data={salesData} width={253} height={253} />
          </div>
          <div className="crm-link-container">
            <Link to="/Salesforchief" className="crm-link">Marketing Followup</Link>
            <Link to="/locationtracking" className="crm-link">Location Tracking</Link>
            <Link to="/locationtracking2" className="crm-link">Location Data</Link>
                        <Link to="/Reports" className="crm-link">Reports</Link>

          </div>
        </div>

        {/* Financial Management Section */}
        <div className="crm-dashboard-section">
          <h2>Financial Management</h2>
          <div className="crm-chart-container crm-chart-container-display">
            <Bar data={financialData} width={253} height={253} />
          </div>
          <div className="crm-link-container">
            <Link to="/Revenue" className="crm-link">Revenue</Link>
            {/* adding s letter at last(file name) */}

            {/* <Link to="/financial_expense" className="crm-link">Expenses</Link> */}
            <Link to="/Invoicemanagement" className="crm-link">Invoice Management</Link>
            <Link to="/Expenseentry" className="crm-link">Expense Entry</Link>
<Link to ="/Mainpage" className="crm-link">Main</Link>
            <Link to="/Budgeting" className="crm-link">Budgeting</Link>

          </div>
        </div>
 
        {/* Sales */}
         <div className="crm-dashboard-section">
  <h2>Sales</h2>
  <div className="crm-chart-container crm-chart-container-display">
    <Bar data={salesData} width={253} height={253} />
  </div>
  <div className="crm-link-container">
<Link to="/MouOrderForm" className="crm-link">
  MOU Process
</Link>
    <Link to="/UpcomingPayments" className="crm-link">Upcoming Payment Dates</Link>
    <Link to="/form1" className="crm-link">form</Link>
    <Link to="/PaymentLink" className="crm-link">Payment Link</Link>
    <Link to="/centralizationdata" className="crm-link">Centralization Data</Link>
  </div>
</div>

        {/* Support Section */}
        <div className="crm-dashboard-section">
          <h2>Support & Maintenance</h2>
          <div className="crm-chart-container">
            <Pie data={supportData} options={pieOptions} width={253} height={253} />
          </div>
          <div className="crm-link-container">
            <Link to="/Ticketsystem" className="crm-link">Ticketing System</Link>
            <Link to="/Productupdates" className="crm-link">Product Updates</Link>
            <Link to="/Bugtracking" className="crm-link">Bug Tracking</Link>
            
          </div>
        </div>
       
      </div>
    </div>
  );
};

export default NewPage;
